/**
 * Formik-bound QueryBuilder wrapper for a claim milestone's applicability conditions.
 * Sibling of `CommunicationConditionsQueryBuilderFormik` — same skeleton (read the
 * `conditions_json` value, gate errors on `submitCount`, gate the catalog fetch on
 * the scope prerequisites, fire `onFieldsLoaded` / `onLoadingChange`, render the
 * pure QB).
 *
 * Differences from the communications sibling:
 *  - No event dispatches a milestone's conditions, so the prerequisites never ask
 *    for one and no date condition can target an event time.
 *  - Reads the milestones' own catalog route rather than the shared
 *    `rules_engine/condition_fields` one, which is gated on SYSCONFIG_AUTOMATION,
 *    a permission the Claim Milestones screen does not require. The scope is sent
 *    from the form, because it is saved only with the milestone.
 */

import React, { useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { useFormikContext } from 'formik';

import useOrganization from '~/components/OrganizationContext';
import { checkConditionPrerequisites } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/prerequisites';
import RuleConditionsQueryBuilder from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/types';
import { useConditionFieldsFetcher } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/useConditionFieldsFetcher';

import { getClaimMilestoneConditionFieldsUrl } from '../apiService';
import TEXTS from '../texts';

interface ClaimMilestoneConditionsQueryBuilderFormikProps {
  /** The milestone's scope as the form holds it, which the save validates the conditions against. */
  isAllLobs: boolean;
  lobs: string[];
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Lets the parent form validate the tree against the fields the builder offered. */
  onFieldsLoaded: (fields: FieldDefinitionDto[]) => void;
  /** Lets the parent dialog disable Save while the catalog is in flight. */
  onLoadingChange: (isLoading: boolean) => void;
}

const ClaimMilestoneConditionsQueryBuilderFormik: React.FC<ClaimMilestoneConditionsQueryBuilderFormikProps> = ({
  isAllLobs,
  lobs,
  isAllSubOrgs,
  subOrgIds,
  onFieldsLoaded,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext<{ conditions_json: RuleGroupType | null }>();

  // `useOrganization` is exported from a JSX file without types; cast at the boundary.
  const { organizationId, subOrganizationEnabled } = useOrganization() as {
    organizationId: number;
    subOrganizationEnabled?: boolean;
  };

  const { prerequisitesMet, prerequisiteMessage } = checkConditionPrerequisites(
    null,
    isAllLobs,
    lobs,
    isAllSubOrgs,
    subOrgIds,
    !!subOrganizationEnabled
  );

  const { fields, isLoading } = useConditionFieldsFetcher({
    url: getClaimMilestoneConditionFieldsUrl(organizationId),
    enabled: prerequisitesMet,
    queryParams: { is_all_lobs: isAllLobs, lobs, is_all_sub_orgs: isAllSubOrgs, sub_org_ids: subOrgIds },
  });

  // Each depends on the value only: a caller passing a non-memoized callback would otherwise loop.
  useEffect(() => {
    onFieldsLoaded(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  useEffect(() => {
    onLoadingChange(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={values.conditions_json}
      onChange={(tree) => setFieldValue('conditions_json', tree)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage={TEXTS.noConditionFields}
    />
  );
};

export default ClaimMilestoneConditionsQueryBuilderFormik;
